import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Settings, Save, Globe, Key, Building2, Eye, EyeOff, 
  CheckCircle, AlertCircle, RefreshCw, Database, Car
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('autogutachter24_token');

const AdminEinstellungen = () => {
  const [activeTab, setActiveTab] = useState('firma');
  const [loading, setLoading] = useState(false);
  const [testingDat, setTestingDat] = useState(false);
  const [datStatus, setDatStatus] = useState(null);
  
  // Firmeneinstellungen
  const [settings, setSettings] = useState({
    firmenname: 'Autogutachter24',
    email: 'info@autogutachter24.de',
    telefon: '',
    adresse: '',
    steuernummer: '',
    ust_id: '',
    bankverbindung: '',
    standard_mwst: '19'
  });

  // DAT Credentials
  const [datCredentials, setDatCredentials] = useState({
    customer_number: '',
    username: '',
    password: '',
    interface_partner_number: '1348925',
    interface_partner_signature: ''
  });
  const [showDatPassword, setShowDatPassword] = useState(false);

  // net.casion Credentials (Restwertbörse)
  const [casionCredentials, setCasionCredentials] = useState({
    customer_id: '',
    user_id: '',
    password: ''
  });
  const [showCasionPassword, setShowCasionPassword] = useState(false);

  // Lade gespeicherte Credentials beim Mount
  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/credentials`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.dat) {
          setDatCredentials(prev => ({
            ...prev,
            customer_number: data.dat.customer_number || '',
            username: data.dat.username || '',
            password: data.dat.password ? '••••••••' : '',
            interface_partner_number: data.dat.interface_partner_number || '1348925',
            interface_partner_signature: data.dat.interface_partner_signature || ''
          }));
          setDatStatus(data.dat.is_valid ? 'valid' : null);
        }
        if (data.casion) {
          setCasionCredentials(prev => ({
            ...prev,
            customer_id: data.casion.customer_id || '',
            user_id: data.casion.user_id || '',
            password: data.casion.password ? '••••••••' : ''
          }));
        }
        if (data.firma) {
          setSettings(prev => ({ ...prev, ...data.firma }));
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Credentials:', error);
    }
  };

  // DAT Credentials speichern
  const saveDatCredentials = async () => {
    setLoading(true);
    try {
      const payload = {
        type: 'dat',
        credentials: {
          customer_number: datCredentials.customer_number,
          username: datCredentials.username,
          password: datCredentials.password === '••••••••' ? null : datCredentials.password,
          interface_partner_number: datCredentials.interface_partner_number,
          interface_partner_signature: datCredentials.interface_partner_signature
        }
      };

      const response = await fetch(`${API_URL}/api/settings/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('DAT Zugangsdaten gespeichert');
        setDatStatus(null); // Status zurücksetzen bis zum nächsten Test
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // DAT Verbindung testen
  const testDatConnection = async () => {
    setTestingDat(true);
    setDatStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/settings/test-dat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setDatStatus('valid');
        toast.success('DAT Verbindung erfolgreich!');
      } else {
        setDatStatus('invalid');
        toast.error(data.error || 'DAT Verbindung fehlgeschlagen');
      }
    } catch (error) {
      setDatStatus('invalid');
      toast.error('Verbindungstest fehlgeschlagen: ' + error.message);
    } finally {
      setTestingDat(false);
    }
  };

  // net.casion Credentials speichern
  const saveCasionCredentials = async () => {
    setLoading(true);
    try {
      const payload = {
        type: 'casion',
        credentials: {
          customer_id: casionCredentials.customer_id,
          user_id: casionCredentials.user_id,
          password: casionCredentials.password === '••••••••' ? null : casionCredentials.password
        }
      };

      const response = await fetch(`${API_URL}/api/settings/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('net.casion Zugangsdaten gespeichert');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Firmeneinstellungen speichern
  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/firma`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Firmeneinstellungen gespeichert');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Settings className="w-6 h-6 text-blue-600" />
              Einstellungen
            </h1>
            <p className="text-gray-500 mt-1">System- und API-Einstellungen verwalten</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-100 p-1 rounded-lg mb-6">
            <TabsTrigger value="firma" className="data-[state=active]:bg-white data-[state=active]:shadow px-6">
              <Building2 className="w-4 h-4 mr-2" />
              Firma
            </TabsTrigger>
            <TabsTrigger value="dat" className="data-[state=active]:bg-white data-[state=active]:shadow px-6">
              <Car className="w-4 h-4 mr-2" />
              DAT myClaim
            </TabsTrigger>
            <TabsTrigger value="casion" className="data-[state=active]:bg-white data-[state=active]:shadow px-6">
              <Database className="w-4 h-4 mr-2" />
              Restwertbörse
            </TabsTrigger>
          </TabsList>

          {/* Firma Tab */}
          <TabsContent value="firma">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-200 bg-blue-50">
                <CardTitle className="text-gray-800 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                  Firmeninformationen
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Firmenname</label>
                    <Input
                      value={settings.firmenname}
                      onChange={(e) => setSettings(prev => ({ ...prev, firmenname: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">E-Mail</label>
                    <Input
                      type="email"
                      value={settings.email}
                      onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Telefon</label>
                    <Input
                      value={settings.telefon}
                      onChange={(e) => setSettings(prev => ({ ...prev, telefon: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Steuernummer</label>
                    <Input
                      value={settings.steuernummer}
                      onChange={(e) => setSettings(prev => ({ ...prev, steuernummer: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">USt-ID</label>
                    <Input
                      value={settings.ust_id}
                      onChange={(e) => setSettings(prev => ({ ...prev, ust_id: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Standard MwSt. (%)</label>
                    <Input
                      value={settings.standard_mwst}
                      onChange={(e) => setSettings(prev => ({ ...prev, standard_mwst: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Adresse</label>
                    <Input
                      value={settings.adresse}
                      onChange={(e) => setSettings(prev => ({ ...prev, adresse: e.target.value }))}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Bankverbindung</label>
                    <Input
                      value={settings.bankverbindung}
                      onChange={(e) => setSettings(prev => ({ ...prev, bankverbindung: e.target.value }))}
                      placeholder="IBAN, Bank, BIC"
                      className="border-gray-300"
                    />
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <Button onClick={saveSettings} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-2" />
                    Speichern
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DAT myClaim Tab */}
          <TabsContent value="dat">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <Car className="w-5 h-5 mr-2" />
                    DAT myClaim Zugangsdaten
                  </div>
                  {datStatus === 'valid' && (
                    <span className="flex items-center gap-1 text-sm bg-green-500 text-white px-3 py-1 rounded-full">
                      <CheckCircle className="w-4 h-4" />
                      Verbunden
                    </span>
                  )}
                  {datStatus === 'invalid' && (
                    <span className="flex items-center gap-1 text-sm bg-red-500 text-white px-3 py-1 rounded-full">
                      <AlertCircle className="w-4 h-4" />
                      Fehler
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Hinweis:</strong> Sie benötigen einen gültigen DAT myClaim Account. 
                    Die Zugangsdaten erhalten Sie von DAT Deutschland.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      <Key className="w-3 h-3 inline mr-1" />
                      Kundennummer
                    </label>
                    <Input
                      value={datCredentials.customer_number}
                      onChange={(e) => setDatCredentials(prev => ({ ...prev, customer_number: e.target.value }))}
                      placeholder="z.B. 1348925"
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Benutzername
                    </label>
                    <Input
                      value={datCredentials.username}
                      onChange={(e) => setDatCredentials(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="DAT Benutzername"
                      className="border-gray-300"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Passwort
                    </label>
                    <div className="relative">
                      <Input
                        type={showDatPassword ? 'text' : 'password'}
                        value={datCredentials.password}
                        onChange={(e) => setDatCredentials(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="DAT Passwort"
                        className="border-gray-300 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowDatPassword(!showDatPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showDatPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Erweiterte Einstellungen */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Erweiterte Einstellungen (Optional)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Interface Partner Nummer</label>
                      <Input
                        value={datCredentials.interface_partner_number}
                        onChange={(e) => setDatCredentials(prev => ({ ...prev, interface_partner_number: e.target.value }))}
                        placeholder="1348925"
                        className="border-gray-300 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Interface Partner Signatur</label>
                      <Input
                        type="password"
                        value={datCredentials.interface_partner_signature}
                        onChange={(e) => setDatCredentials(prev => ({ ...prev, interface_partner_signature: e.target.value }))}
                        placeholder="Signatur (falls vorhanden)"
                        className="border-gray-300 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={testDatConnection} 
                    disabled={testingDat || !datCredentials.username}
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    {testingDat ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Teste Verbindung...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Verbindung testen
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={saveDatCredentials} 
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    DAT Zugangsdaten speichern
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Restwertbörse (net.casion) Tab */}
          <TabsContent value="casion">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600">
                <CardTitle className="text-white flex items-center">
                  <Database className="w-5 h-5 mr-2" />
                  net.casion Restwertbörse Zugangsdaten
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="bg-orange-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Hinweis:</strong> Die Zugangsdaten für die Restwertbörse erhalten Sie von net.casion. 
                    Diese werden für die automatische Einstellung von Fahrzeugen benötigt.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      <Key className="w-3 h-3 inline mr-1" />
                      Kunden-ID (CustomerID)
                    </label>
                    <Input
                      value={casionCredentials.customer_id}
                      onChange={(e) => setCasionCredentials(prev => ({ ...prev, customer_id: e.target.value }))}
                      placeholder="z.B. 12345"
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Benutzer-ID (UserID)
                    </label>
                    <Input
                      value={casionCredentials.user_id}
                      onChange={(e) => setCasionCredentials(prev => ({ ...prev, user_id: e.target.value }))}
                      placeholder="z.B. 67890"
                      className="border-gray-300"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Passwort
                    </label>
                    <div className="relative">
                      <Input
                        type={showCasionPassword ? 'text' : 'password'}
                        value={casionCredentials.password}
                        onChange={(e) => setCasionCredentials(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="net.casion Passwort"
                        className="border-gray-300 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCasionPassword(!showCasionPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCasionPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={saveCasionCredentials} 
                    disabled={loading}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Restwertbörse Zugangsdaten speichern
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminEinstellungen;
